/**
 * Concurrency helpers
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * IIFE providing `window.Theme.latestOnly` — a per-key Last-Write-Wins runner
 * with AbortController. Ported from shopify-framework's fw-concurrency.js as the
 * concurrency primitive the `<cart-engine>` (assets/cart-engine.js) relies on
 * for rapid-mutation correctness (issue #20 cart-engine migration).
 *
 * WHY IIFE (not ES module): it must be available on `window.Theme` during the
 * defer phase, before Alpine fires `alpine:init` and before the cart-engine
 * module loads. Same constraint as fw-events.js / alpine-stores.js.
 *
 * Two concurrency layers guard the cart (see cart-engine.js `_postCart`):
 *   1. this per-key LWW — collapses same-row spam to the last write, aborting
 *      the prior in-flight request for that key.
 *   2. a mutation serializer chain (in the engine) — one POST in flight at a
 *      time so full-cart snapshots apply in server order.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  // Per-key runtime state. Lazily created on first call for a given key.
  // Shape: { [key]: { version: number, controller: AbortController | null } }
  const state = Object.create(null);

  /**
   * Run an async fn under per-key Last-Write-Wins semantics.
   *
   * Each call increments a version counter scoped to `key` and aborts the
   * previous in-flight request for that key (independent from other keys).
   * The returned promise resolves to:
   *   - `{ result, isLatest: true }`  — this call's response is the latest
   *   - `{ result, isLatest: false }` — a newer call for this key superseded
   *                                     this one before it resolved
   *   - `null`                        — this call was aborted (AbortError);
   *                                     callers should treat as "no-op"
   *
   * Non-abort errors propagate (re-thrown).
   *
   * Contract: asyncFn receives one argument — the AbortSignal scoped to this
   * call — and MUST forward it to fetch so the abort cancels the work, not just
   * the bookkeeping.
   *
   * Per-key isolation: latestOnly('cart:add', ...) and
   * latestOnly('cart:line:<key>', ...) do NOT share queues.
   *
   * @param {string} key
   * @param {(signal: AbortSignal) => Promise<unknown>} asyncFn
   * @returns {Promise<{ result: unknown, isLatest: boolean } | null>}
   */
  async function latestOnly(key, asyncFn) {
    if (typeof key !== 'string' || !key) {
      throw new TypeError('latestOnly: key must be a non-empty string');
    }
    if (typeof asyncFn !== 'function') {
      throw new TypeError('latestOnly: asyncFn must be a function');
    }

    if (!state[key]) {
      state[key] = { version: 0, controller: null };
    }
    const keyState = state[key];

    // Increment version for this call, abort the previous in-flight one.
    const thisVersion = ++keyState.version;
    if (keyState.controller) {
      keyState.controller.abort();
    }
    keyState.controller = new AbortController();

    try {
      const result = await asyncFn(keyState.controller.signal);

      // Only surface the result if a newer call hasn't superseded us in flight.
      const isLatest = thisVersion === keyState.version;
      return {
        result: isLatest ? result : null,
        isLatest,
      };
    } catch (err) {
      // Aborted (a newer call fired) → resolve to null silently.
      if (err && err.name === 'AbortError') {
        return null;
      }
      throw err;
    }
  }

  window.Theme.latestOnly = latestOnly;

  // For tests / debugging.
  window.Theme._concurrencyState = state;
})();
